import { SITE_URL, ORG_ID, absoluteUrl } from "@/lib/site";

export const CATALOGUE_ID = `${SITE_URL}/packages#catalogue`;

/**
 * Charge model: price is delivery hours multiplied by a rate that reflects the
 * size and complexity of the client. Work running a quarter or longer is charged
 * per month. Shorter work is a fixed one-off price.
 */
export type ChargeBasis = "one-off" | "per-month";

/**
 * CONFIRMED: signed off and safe to publish.
 * VARIABLE: deliberately a band, safe to publish.
 * PROPOSED: not confirmed, never published.
 * UNCONFIRMED: figure exists internally but is not approved, never published.
 */
export type PricingStatus = "CONFIRMED" | "PROPOSED" | "UNCONFIRMED" | "VARIABLE";

export type PricingRow = {
  /** The service line or tier the item belongs to. */
  service: string;
  path: string;
  /** The item name as sold. Unique across the ladder. */
  name: string;
  charge: ChargeBasis;
  /** Lower figure in AUD plus GST, or null where not confirmed. */
  lower: number | null;
  /** Upper figure in AUD plus GST, or null where the item is a single figure or not confirmed. */
  upper: number | null;
  status: PricingStatus;
};

/**
 * A row may only be shown with a figure when the status allows it and a lower
 * figure exists. Everything else renders as a name and a link, with no figure.
 */
export const isQuotable = (row: PricingRow): boolean =>
  (row.status === "CONFIRMED" || row.status === "VARIABLE") && row.lower !== null;

const money = (n: number) => `$${n.toLocaleString("en-AU")}`;

/** Display string for a quotable row. Returns null where the row is not quotable. */
export const formatPrice = (row: PricingRow): string | null => {
  if (!isQuotable(row) || row.lower === null) return null;
  if (row.lower === 0 && row.upper === null) return "Free";
  const base = row.upper !== null ? `${money(row.lower)} to ${money(row.upper)}` : money(row.lower);
  return row.charge === "per-month" ? `from ${base} per month plus GST` : `from ${base} plus GST`;
};


const r = (
  service: string,
  path: string,
  name: string,
  charge: ChargeBasis,
  lower: number | null,
  upper: number | null,
  status: PricingStatus,
): PricingRow => ({ service, path, name, charge, lower, upper, status });

/**
 * The single price ladder. Every dollar figure published anywhere on the site
 * resolves to exactly one row here. Figures are AUD plus GST.
 */
export const pricingRows: PricingRow[] = [
  // Diagnosis, three rungs
  r("Diagnosis", "/scan", "Operating Scan", "one-off", 0, null, "CONFIRMED"),
  r("Diagnosis", "/packages", "Operating Diagnostic", "one-off", 3600, null, "CONFIRMED"),
  r("Diagnosis", "/packages", "Full Diagnostic and 90-Day Roadmap", "one-off", 6900, null, "CONFIRMED"),

  // 90-Day Program, the diagnosis and the install as one fixed-fee engagement
  r("90-Day Program", "/packages", "90-Day Operating System Install", "one-off", 36000, null, "CONFIRMED"),

  // Decision Sprint, stage 1 beside the diagnosis ladder
  r("Decision Sprint", "/decision-sprint", "Rapid Decision Review", "one-off", 3000, null, "CONFIRMED"),
  r("Decision Sprint", "/decision-sprint", "Decision Sprint", "one-off", 6300, null, "CONFIRMED"),
  r("Decision Sprint", "/decision-sprint", "Deep Decision Case", "one-off", 9000, null, "CONFIRMED"),

  // Commercial and Pricing
  r("Commercial and Pricing", "/services/performance-discipline", "Pricing and Margin Review", "one-off", 6900, null, "CONFIRMED"),
  r("Commercial and Pricing", "/services/performance-discipline", "Rolling Cash Forecast, 13 weeks", "one-off", 3600, null, "CONFIRMED"),
  r("Commercial and Pricing", "/services/strategy-execution", "Annual Operating Budget", "one-off", 5400, null, "CONFIRMED"),
  r("Commercial and Pricing", "/services/team-structure", "Remuneration and Incentive Design", "one-off", 4500, null, "CONFIRMED"),

  // Operating System & Governance
  r("Operating System & Governance", "/services/operating-system", "Meeting Cadence Install", "one-off", 5400, null, "CONFIRMED"),
  r("Operating System & Governance", "/services/operating-system", "Operating System & Governance install", "one-off", 18000, null, "CONFIRMED"),

  // People, Culture & Accountability
  r("People, Culture & Accountability", "/services/team-structure", "Org Design Sprint", "one-off", 5400, null, "CONFIRMED"),
  r("People, Culture & Accountability", "/services/team-structure", "People, Culture & Accountability install", "one-off", 15000, null, "CONFIRMED"),

  // Strategy to Execution
  r("Strategy to Execution", "/services/strategy-execution", "Quarterly Planning Day", "one-off", 3900, null, "CONFIRMED"),
  r("Strategy to Execution", "/services/strategy-execution", "90-Day Execution Sprint", "one-off", 9900, null, "CONFIRMED"),
  r("Strategy to Execution", "/services/strategy-execution", "1-Day Strategy Session", "one-off", 5400, null, "CONFIRMED"),
  r("Strategy to Execution", "/services/strategy-execution", "Annual Growth Plan", "one-off", 12000, null, "CONFIRMED"),

  // Performance & Commercial Discipline
  r("Performance & Commercial Discipline", "/services/performance-discipline", "Leadership Scorecard Build", "one-off", 4500, null, "CONFIRMED"),
  r("Performance & Commercial Discipline", "/services/performance-discipline", "Performance & Commercial Discipline install", "one-off", 9900, null, "CONFIRMED"),

  // Project & Initiative Leadership
  r("Project & Initiative Leadership", "/services/project-leadership", "Scoped per initiative", "per-month", 3600, null, "VARIABLE"),

  // Systems & Operational Improvement
  r("Systems & Operational Improvement", "/services/systems-improvement", "One process, one fix", "one-off", 3000, null, "CONFIRMED"),
  r("Systems & Operational Improvement", "/services/systems-improvement", "Systems & Operational Improvement install", "one-off", 7500, null, "CONFIRMED"),

  // Culture, values and sentiment, delivered inside People, Culture & Accountability
  r("People, Culture & Accountability", "/services/team-structure", "Values and sentiment baseline", "one-off", null, null, "PROPOSED"),
  r("People, Culture & Accountability", "/services/team-structure", "Culture and sentiment install", "per-month", null, null, "PROPOSED"),

  r("One-offs", "/services/partner-alignment", "Partner Alignment Review", "one-off", 9900, null, "CONFIRMED"),
  r("One-offs", "/services/structure-and-performance-reset", "Structure and Performance Reset", "one-off", 12600, null, "CONFIRMED"),

  // One-off programs
  r("One-offs", "/services", "Service line install", "one-off", 7500, 18000, "VARIABLE"),

  // Retainers
  r("Retainers", "/packages", "Fortnightly rhythm base", "per-month", 3900, null, "CONFIRMED"),
  r("Retainers", "/packages", "Weekly rhythm base", "per-month", 6900, null, "CONFIRMED"),
  // Size signal only. Not sold as a separate item and not published in the offer catalogue.
  r("Retainers", "/packages", "Weekly rhythm base, businesses above $15M", "per-month", 9900, null, "VARIABLE"),
  r("Retainers", "/packages", "Each additional active service line", "per-month", 2100, 3000, "VARIABLE"),
  r("Retainers", "/packages", "Composed Integrator", "per-month", 3900, null, "VARIABLE"),
  r("Retainers", "/packages", "Full embedded Integrator seat", "per-month", null, null, "PROPOSED"),
];

/**
 * Names used elsewhere in the site before a row was renamed or retired. Lookups
 * resolve through here so a rename cannot break a caller.
 */
const legacyNames: Record<string, string> = {
  "Operational Diagnostic": "Operating Diagnostic",
  "Operating Assessment": "Operating Diagnostic",
  "Operating Assessment, 1 to 3 respondents": "Operating Diagnostic",
  "Operating Assessment, 4 to 9 respondents": "Operating Diagnostic",
  "Operating Assessment, 10 to 18 respondents": "Operating Diagnostic",
  "Operating Assessment, 19 or more respondents": "Operating Diagnostic",
  "On-site group debrief": "Operating Diagnostic",
  "Monthly rhythm base": "Fortnightly rhythm base",
  "Integrator Retainer, typical composed monthly fee": "Composed Integrator",
  "Team Structure & Accountability install": "People, Culture & Accountability install",
  "Equilibrium, Culture and People install": "Culture and sentiment install",
};

/** Look up a row by its unique name. Throws where the name is unknown. */
export const findRow = (name: string): PricingRow => {
  const canonical = legacyNames[name] ?? name;
  const match = pricingRows.find((row) => row.name === canonical);
  if (!match) throw new Error(`Unknown pricing row: ${name}`);
  return match;
};


export const rowsForService = (service: string): PricingRow[] =>
  pricingRows.filter((row) => row.service === service);

export const slugify = (s: string) =>
  s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

const priceSpec = (row: PricingRow) => {
  const isRange = row.upper !== null;
  const figures = isRange
    ? { minPrice: String(row.lower), maxPrice: String(row.upper) }
    : { price: String(row.lower) };

  if (row.charge === "per-month") {
    return {
      "@type": "UnitPriceSpecification",
      ...figures,
      priceCurrency: "AUD",
      valueAddedTaxIncluded: false,
      unitCode: "MON",
      billingDuration: 1,
      referenceQuantity: { "@type": "QuantitativeValue", value: 1, unitCode: "MON" },
    };
  }

  return {
    "@type": "PriceSpecification",
    ...figures,
    priceCurrency: "AUD",
    valueAddedTaxIncluded: false,
  };
};

type NodeMeta = {
  /** Stable id fragment, sourced from the tier option so it cannot drift. */
  id: string;
  /** Tier name, published as the Offer category. */
  category: string;
};

/**
 * A quotable row becomes a priced Offer node. Description states the figure
 * exactly as the page renders it, so schema can never contradict the page.
 */
export const offerNodeFor = (row: PricingRow, description: string, meta: NodeMeta) => ({
  "@type": "Offer",
  "@id": `${SITE_URL}/packages#offer-${slugify(meta.id)}`,
  name: row.name,
  description,
  category: meta.category,
  priceCurrency: "AUD",
  valueAddedTaxIncluded: false,
  availability: "https://schema.org/InStock",
  url: absoluteUrl(row.path),
  seller: { "@id": ORG_ID },
  ...(row.upper === null ? { price: String(row.lower) } : {}),
  priceSpecification: priceSpec(row),
  itemOffered: {
    "@type": "Service",
    name: row.name,
    description,
    provider: { "@id": ORG_ID },
    url: absoluteUrl(row.path),
  },
});

/**
 * A row without a confirmed figure still appears as an Offer, so the catalogue
 * stays homogeneous, but carries no price and no priceSpecification.
 */
export const unpricedOfferNodeFor = (row: PricingRow, description: string, meta: NodeMeta) => ({
  "@type": "Offer",
  "@id": `${SITE_URL}/packages#offer-${slugify(meta.id)}`,
  name: row.name,
  description,
  category: meta.category,
  availability: "https://schema.org/InStock",
  url: absoluteUrl(row.path),
  seller: { "@id": ORG_ID },
  itemOffered: {
    "@type": "Service",
    name: row.name,
    description,
    provider: { "@id": ORG_ID },
    url: absoluteUrl(row.path),
  },
});

